import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Global } from '@emotion/react';
import { Button } from '@domain-group/fe-blueprint-ui/button';
import { Radio } from '@domain-group/fe-blueprint-ui/form';
import { FindAnAgentIcon } from '@domain-group/fe-co-icon';
import {
  Pill,
  Pills,
  StickyContent,
} from '@domain-group/fe-co-horizontal-dropdown-pills';
import { PROPERTY_MANAGER, SALES_AGENT } from '../search-filter-bar/enums';
import * as styles from './agent-type-filter.style';

const PILL_KEY = 'agent-type';
const GROUP_NAME = 'agent-type-selection';
const HEADING_ID = `label_${GROUP_NAME}`;
const TITLE = "I'm looking to";
const PANEL_WIDTH = 450;

const OPTIONS = [
  { value: SALES_AGENT, label: 'Sell a home', triggerLabel: 'Sell' },
  { value: PROPERTY_MANAGER, label: 'Lease a property', triggerLabel: 'Lease' },
];

interface AgentTypeFilterProps {
  currentAgentType: string;
  onSelect: (agentType: string) => void;
}

const AgentTypeFilter = ({
  currentAgentType,
  onSelect,
}: AgentTypeFilterProps): JSX.Element => {
  const [openPill, setOpenPill] = useState<string | null>(null);
  const [formAgentType, setFormAgentType] = useState(currentAgentType);
  const filterRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    setFormAgentType(currentAgentType);
  }, [currentAgentType, openPill]);

  useEffect(() => {
    if (openPill) {
      wasOpen.current = true;
      return;
    }

    if (wasOpen.current) {
      wasOpen.current = false;
      filterRef.current?.querySelector('button')?.focus();
    }
  }, [openPill]);

  const close = () => setOpenPill(null);

  const apply = (event: SyntheticEvent) => {
    event.preventDefault();
    onSelect(formAgentType);
    close();
  };

  const selected =
    OPTIONS.find((option) => option.value === currentAgentType) ?? OPTIONS[0];

  return (
    <div css={styles.filter} ref={filterRef} data-testid="agent-type-filter">
      <Global styles={styles.pinnedPopoutRespectsPageMargin} />
      <Pills open={openPill} onOpenChange={setOpenPill}>
        <Pill
          key={PILL_KEY}
          label={selected.triggerLabel}
          labelledBy={HEADING_ID}
          active={Boolean(currentAgentType)}
          maxWidth={PANEL_WIDTH}
        >
          <div css={styles.panel}>
            <div css={styles.title} id={HEADING_ID}>
              <FindAnAgentIcon size="small" aria-hidden />
              {TITLE}
            </div>
            <div css={styles.options} role="radiogroup" aria-label={TITLE}>
              {OPTIONS.map(({ value, label }) => (
                <div key={value} css={styles.radioRow}>
                  <Radio
                    id={`${GROUP_NAME}_${value}`}
                    name={GROUP_NAME}
                    value={value}
                    labelContent={<span css={styles.optionLabel}>{label}</span>}
                    checked={value === formAgentType}
                    onChange={() => setFormAgentType(value)}
                  />
                </div>
              ))}
            </div>
          </div>
          <StickyContent>
            <div css={styles.actions}>
              <Button
                type="button"
                appearance="outline"
                onClick={close}
                css={styles.cancelButton}
              >
                Cancel
              </Button>
              <Button type="button" onClick={apply}>
                Apply
              </Button>
            </div>
          </StickyContent>
        </Pill>
      </Pills>
    </div>
  );
};

export default AgentTypeFilter;
